import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { api } from "../../data/api";
import { useMastersStore, selectUnreadCount } from "../../data/mastersStore";
import { useAuth } from "../../auth/AuthContext";
import { useDivisionMode } from "../ui";
import antmileLogo from "../../assets/antmile_logo.svg";

const ROLE_LABEL: Record<string, string> = {
  admin: "Administrador",
  coordinator: "Coordinador",
  supervisor: "Supervisor",
  driver: "Chofer",
};

type NavItem = {
  to: string;
  label: string;
  /** When true the link is greyed out and not clickable. */
  disabled?: boolean;
  /** Tooltip when disabled. */
  disabledHint?: string;
  /** Optional right-side badge — numeric counters (unread alerts, etc.) */
  badge?: number;
};

// Left navigation. Dos módulos principales:
//   - Planeación: carga CSV + asignación choferes→clientes (lo que el
//     planner hace antes de salir a calle)
//   - Última milla: tracking, monitoreo y avance en vivo de los choferes
//     ya en ruta
// Más Maestros (datos de configuración) y Análisis (reportes/alertas).
export function Sidebar() {
  const connected = useBackendHealth();
  const unreadAlerts = useMastersStore(selectUnreadCount);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  return (
    <aside className="w-56 bg-sidebar text-sidebar-ink flex flex-col flex-shrink-0 h-full">
      <div className="px-4 py-4 border-b border-white/10">
        <div className="flex items-center gap-2.5">
          <img
            src={antmileLogo}
            alt="antMile"
            className="w-9 h-9 rounded-md bg-white/95 p-0.5 shadow-sm"
          />
          <div className="leading-tight">
            <div className="font-semibold text-base text-white">
              ant<span className="text-secondary">Mile</span>
            </div>
            <div className="text-[9px] uppercase tracking-wider text-sidebar-sub">
              Logística PYME
            </div>
          </div>
        </div>
        {currentUser ? (
          <div className="mt-2">
            <div className="text-sm text-white truncate" title={currentUser.full_name}>
              {currentUser.full_name}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-sidebar-sub">
              {ROLE_LABEL[currentUser.role] ?? currentUser.role}
            </div>
          </div>
        ) : (
          <div className="text-[10px] uppercase tracking-wider text-sidebar-sub mt-0.5">
            Plataforma logística
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-3">
        <NavSection title="Planeación">
          <NavLinkRow to="/programacion" label="Programación" />
          {currentUser?.role === "admin" && (
            <NavLinkRow to="/admin/optimizer-configs" label="Config. optimizador" />
          )}
        </NavSection>

        <NavSection title="Última milla">
          <NavLinkRow to="/ultima-milla/importar" label="Importar planificación" />
          <NavLinkRow to="/tracking" label="Operación en vivo" />
          {(currentUser?.role === "coordinator" || currentUser?.role === "admin") && (
            <NavLinkRow to="/coordinador" label="Equipo (Coordinador)" />
          )}
          <NavLinkRow to="/supervisor" label="Avance / Supervisión" />
          <NavLinkRow to="/fleet" label="Vehículos en ruta" />
          <NavLinkRow to="/orders" label="Órdenes" />
        </NavSection>

        <NavSection title="Maestros" defaultCollapsed>
          <NavLinkRow to="/drivers" label="Choferes" />
          {(currentUser?.role === "admin" ||
            currentUser?.role === "coordinator") && (
            <>
              <NavLinkRow to="/supervisors" label="Supervisores" />
              <NavLinkRow to="/auto-messages" label="Mensajes automáticos" />
            </>
          )}
          <NavLinkRow to="/vehicles" label="Vehículos" />
          <NavLinkRow to="/warehouses" label="Almacenes" />
          <NavLinkRow to="/clients" label="Clientes" />
        </NavSection>

        <NavSection title="Análisis" defaultCollapsed>
          <NavLinkRow to="/reports" label="Reportes" />
          <NavLinkRow to="/alerts" label="Alertas" badge={unreadAlerts} />
        </NavSection>

        {/* Sección "Administración" eliminada — su único ítem
            (Config. optimizador) migró a Planeación, que es donde
            se programa la ruta y es coherente tenerlo cerca. */}
      </nav>

      <div className="border-t border-white/10">
        {/* Selector global de modo operativo. Vive en el footer, fuera de
            las secciones de navegación, porque afecta a múltiples
            pantallas (Operación en vivo / Avance / Vehículos en ruta /
            Órdenes) — es ortogonal a "qué pantalla estoy mirando".
            Persiste en sessionStorage y se sincroniza entre tabs. */}
        <DivisionSidebarSelector />
        {currentUser && (
          <button
            onClick={handleLogout}
            className="w-full px-4 py-2 text-sm text-sidebar-ink hover:bg-sidebar-hover hover:text-white text-left border-b border-white/5 border-t border-white/10"
          >
            Cerrar sesión
          </button>
        )}
        <div className="px-4 py-2 text-[11px] text-sidebar-sub">
          <div className="flex items-center gap-2">
            <span
              className={`inline-block w-2 h-2 rounded-full ${connected ? "bg-state-delivered" : "bg-state-failed"}`}
              title={connected ? "Backend conectado" : "Sin conexión al backend"}
            />
            <span>{connected ? "Conectado" : "Sin conexión"}</span>
          </div>
          <div className="text-[10px] text-sidebar-sub/70 font-mono mt-0.5 pl-4">
            v{__APP_VERSION__}
          </div>
        </div>
      </div>
    </aside>
  );
}

// Healthcheck del backend: pinguea cada 15s. Reemplaza al indicador
// "Motor conectado" que leía del WebSocket del simulador viejo.
function useBackendHealth(): boolean {
  const [ok, setOk] = useState(true);
  useEffect(() => {
    let alive = true;
    const check = async () => {
      const healthy = await api.ping();
      if (alive) setOk(healthy);
    };
    check();
    const t = setInterval(check, 15_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);
  return ok;
}

// NavSection con toggle colapsable. El estado se persiste por sección en
// localStorage (clave `sidebar:collapsed:<title>`) para que el menú se vea
// igual al recargar. `defaultCollapsed` aplica solo si nunca se tocó la
// sección — secciones secundarias (Maestros, Análisis) arrancan colapsadas
// para reducir ruido visual; las operativas (Planeación, Última milla)
// arrancan abiertas.
function NavSection({
  title,
  children,
  defaultCollapsed = false,
}: {
  title: string;
  children: React.ReactNode;
  defaultCollapsed?: boolean;
}) {
  const storageKey = `sidebar:collapsed:${title}`;
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return defaultCollapsed;
    const stored = window.localStorage.getItem(storageKey);
    if (stored === null) return defaultCollapsed;
    return stored === "1";
  });

  function toggle() {
    setCollapsed((cur) => {
      const next = !cur;
      try {
        window.localStorage.setItem(storageKey, next ? "1" : "0");
      } catch {
        /* localStorage puede fallar en modo incognito — ignoramos */
      }
      return next;
    });
  }

  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={toggle}
        className="w-full px-4 mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider text-sidebar-sub hover:text-white transition-colors"
        aria-expanded={!collapsed}
      >
        <span>{title}</span>
        <span
          className={`text-[8px] transition-transform duration-150 ${
            collapsed ? "-rotate-90" : ""
          }`}
        >
          ▼
        </span>
      </button>
      {!collapsed && <div>{children}</div>}
    </div>
  );
}

/// DivisionSidebarSelector — switch operativo "Consumo / Salud".
/// Vive en el footer del sidebar (encima de "Cerrar sesión") porque es
/// un control transversal: cambia el scope de Operación en vivo / Avance
/// / Vehículos en ruta / Órdenes. El color del activo usa la paleta del
/// flujo (primary = consumo, secondary = salud) para asociar color con
/// división de un vistazo. Microcopy abajo confirma el alcance temporal.
function DivisionSidebarSelector() {
  const [mode, setMode] = useDivisionMode();
  return (
    <div className="px-3 py-2.5 border-b border-white/10">
      <div className="text-[9px] uppercase tracking-wider text-sidebar-sub mb-1.5">
        Modo operativo
      </div>
      <div className="grid grid-cols-2 gap-1">
        <DivisionPill
          label="Consumo"
          active={mode === "CONSUMO"}
          onClick={() => setMode("CONSUMO")}
          activeBg="bg-primary"
        />
        <DivisionPill
          label="Salud"
          active={mode === "SALUD"}
          onClick={() => setMode("SALUD")}
          activeBg="bg-secondary"
        />
      </div>
      <div className="mt-1.5 text-[10px] text-sidebar-sub leading-snug">
        {mode === "CONSUMO" ? (
          <span>Chofer + ruta del día.</span>
        ) : (
          <span>Repartidor · últimos 3 días.</span>
        )}
      </div>
    </div>
  );
}

function DivisionPill({
  label,
  active,
  onClick,
  activeBg,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  activeBg: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2 py-1.5 text-xs font-semibold rounded transition-colors ${
        active
          ? `${activeBg} text-white shadow-sm`
          : "bg-transparent text-sidebar-ink hover:bg-white/5"
      }`}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}

function NavLinkRow({ to, label, disabled, disabledHint, badge }: NavItem) {
  if (disabled) {
    return (
      <div
        className="px-4 py-1.5 text-sm text-sidebar-sub/60 cursor-not-allowed select-none"
        title={disabledHint}
      >
        {label}
      </div>
    );
  }
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center justify-between px-4 py-1.5 text-sm border-l-2 transition-colors ${
          isActive
            ? "bg-sidebar-active border-secondary text-white"
            : "border-transparent text-sidebar-ink hover:bg-sidebar-hover hover:text-white"
        }`
      }
    >
      <span>{label}</span>
      {badge != null && badge > 0 && (
        <span className="bg-secondary text-white text-[10px] font-semibold rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </NavLink>
  );
}
